import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText, Users, ShieldCheck, Wallet, Store, ShoppingCart, Calculator,
  BookOpen, Mail, LogOut, RefreshCw, ChevronRight, Layers,
  Users2,   // v1.7.0 · CRM
  ShieldAlert,   // v1.22.0 · Auditoría interna
} from 'lucide-react';

/**
 * ModuleKanbanMenu
 * ----------------
 * Menú principal en formato Kanban (columnas horizontales continuas).
 * Cada módulo del sistema es una columna con:
 *   - Icono + título + subtítulo
 *   - Gradiente de color propio
 *   - Contador/indicador opcional (badge)
 *   - Estado activo (columna elevada)
 *
 * Se filtra automáticamente por rol de usuario.
 * Props:
 *   - user        Usuario actual (localStorage.user)
 *   - active      ID del módulo activo (para highlight)
 *   - onAction    Callbacks extra: { logout, brochure, fixLinks, refresh }
 *   - compact     Boolean, si true → cards más compactos
 */
const ALL_MODULES = [
  {
    id: 'contratos',
    title: 'Contratos',
    subtitle: 'Propuestas y firmas',
    icon: FileText,
    to: '/contratos',
    grad: 'from-indigo-500 to-blue-600',
    tint: 'bg-indigo-50 border-indigo-200 text-indigo-700',
    roles: ['admin', 'gerencia', 'contratos'],
  },
  {
    id: 'qa',
    title: 'QA',
    subtitle: 'Kanban · Hitos · OKRs',
    icon: ShieldCheck,
    to: '/qa',
    grad: 'from-emerald-500 to-teal-600',
    tint: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    roles: ['admin', 'gerencia', 'qa', 'contratos', 'client_qa'],
  },
  {
    id: 'finanzas',
    title: 'Finanzas',
    subtitle: 'Ventas y Payphone',
    icon: Wallet,
    to: '/finanzas',
    grad: 'from-amber-500 to-orange-600',
    tint: 'bg-amber-50 border-amber-200 text-amber-700',
    roles: ['admin', 'gerencia', 'finanzas'],
  },
  {
    id: 'cobros',
    title: 'Cobros',
    subtitle: 'Planes y cuotas',
    icon: RefreshCw,
    to: '/finanzas/cobros',
    grad: 'from-lime-500 to-green-600',
    tint: 'bg-lime-50 border-lime-200 text-lime-700',
    roles: ['admin', 'gerencia', 'finanzas', 'contratos'],
  },
  {
    id: 'compras',
    title: 'Compras',
    subtitle: 'Flujo de caja',
    icon: ShoppingCart,
    to: '/compras',
    grad: 'from-rose-500 to-red-600',
    tint: 'bg-rose-50 border-rose-200 text-rose-700',
    roles: ['admin', 'gerencia', 'finanzas'],
    requiresModule: 'compras',
  },
  {
    id: 'contabilidad',
    title: 'Contabilidad SRI',
    subtitle: 'IVA · Renta · Anexos',
    icon: Calculator,
    to: '/contabilidad',
    grad: 'from-cyan-500 to-sky-600',
    tint: 'bg-cyan-50 border-cyan-200 text-cyan-700',
    roles: ['admin', 'gerencia', 'finanzas', 'contratos', 'contabilidad'],
    badge: 'Nuevo',
  },
  // v1.7.0 · CRM fase 1: visible por rol o por módulo adicional «crm»
  {
    id: 'crm',
    title: 'CRM',
    subtitle: 'Contactos y oportunidades',
    icon: Users2,
    to: '/crm',
    grad: 'from-indigo-500 to-blue-700',
    tint: 'bg-indigo-50 border-indigo-200 text-indigo-700',
    roles: ['admin', 'gerencia', 'contratos', 'ventas'],
    orModule: 'crm',
    badge: 'Nuevo',
  },
  // v1.22.0 · Auditoría interna: por rol o por módulo adicional «auditoria»
  {
    id: 'auditoria',
    title: 'Auditoría',
    subtitle: 'Banderas rojas e informes IA',
    icon: ShieldAlert,
    to: '/auditoria',
    grad: 'from-slate-600 to-indigo-700',
    tint: 'bg-indigo-50 border-indigo-200 text-indigo-700',
    roles: ['admin', 'gerencia', 'contratos', 'auditoria'],
    orModule: 'auditoria',
    badge: 'Nuevo',
  },
  {
    id: 'mercado',
    title: 'Mercado',
    subtitle: 'Analítica de mercado',
    icon: Store,
    to: '/mercado',
    grad: 'from-purple-500 to-fuchsia-600',
    tint: 'bg-purple-50 border-purple-200 text-purple-700',
    roles: ['admin', 'gerencia', 'mercado', 'mercado_consulta'],
  },
  {
    id: 'usuarios',
    title: 'Usuarios',
    subtitle: 'Roles y permisos',
    icon: Users,
    to: '/usuarios',
    grad: 'from-slate-500 to-gray-700',
    tint: 'bg-slate-50 border-slate-200 text-slate-700',
    roles: ['admin', 'gerencia'],
    onlyGerencia: true,
  },
  {
    id: 'emails',
    title: 'Emails',
    subtitle: 'Config. plantillas',
    icon: Mail,
    to: '/configuracion/emails',
    grad: 'from-pink-500 to-rose-500',
    tint: 'bg-pink-50 border-pink-200 text-pink-700',
    roles: ['admin', 'gerencia'],
    onlyGerencia: true,
  },
];

const filterModules = (user) => {
  if (!user) return [];
  const isGerencia = user.username === 'Gerencia' || user.role === 'admin' || user.role === 'gerencia';
  return ALL_MODULES.filter(m => {
    if (m.onlyGerencia && !isGerencia) return false;
    if (m.requiresModule) {
      const extra = user.extra_modules || [];
      if (!extra.includes(m.requiresModule) && !isGerencia && user.role !== 'finanzas') return false;
    }
    if (m.orModule && (user.extra_modules || []).includes(m.orModule)) return true;   // v1.7.0
    return m.roles.includes(user.role) || isGerencia;
  });
};

export const ModuleKanbanMenu = ({ user, active = null, onAction = {}, compact = false }) => {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const modules = filterModules(user);

  if (modules.length === 0) return null;

  const cardHeight = compact ? 'h-[92px]' : 'h-[124px]';
  const cardWidth = compact ? 'w-[168px]' : 'w-[200px]';

  return (
    <div className="bg-gradient-to-b from-white to-gray-50 border-b border-gray-200" data-testid="module-kanban-menu">
      <div className="max-w-[1600px] mx-auto px-4 py-3">
        {/* Header */}
        <div className="flex items-center gap-2 mb-2">
          <div className="p-1.5 rounded-md bg-[#2F3D8E]/10">
            <Layers className="w-4 h-4 text-[#2F3D8E]" />
          </div>
          <h3 className="text-sm font-bold text-[#0F1225]">Módulos Analytica</h3>
          <span className="text-xs text-gray-500">· desliza para ver más ·</span>
          <button
            onClick={() => setCollapsed(c => !c)}
            className="ml-auto text-xs text-gray-400 hover:text-gray-700"
            data-testid="module-menu-toggle"
          >
            {collapsed ? 'Mostrar ▾' : 'Ocultar ▴'}
          </button>
        </div>

        {!collapsed && (
          <div className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory" data-testid="module-kanban-scroll">
            {modules.map(m => {
              const isActive = active === m.id;
              const Icon = m.icon;
              return (
                <button
                  key={m.id}
                  onClick={() => navigate(m.to)}
                  className={`snap-start flex-shrink-0 ${cardWidth} ${cardHeight} rounded-xl overflow-hidden border-2 group text-left transition-all
                    ${isActive ? 'border-[#0F1225] shadow-xl scale-[1.03]' : 'border-transparent hover:border-gray-300 hover:shadow-md'}`}
                  data-testid={`module-card-${m.id}`}
                >
                  {/* Gradient header */}
                  <div className={`bg-gradient-to-br ${m.grad} px-3 py-2 text-white flex items-center gap-2`}>
                    <div className="p-1.5 rounded-lg bg-white/25 backdrop-blur-sm">
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold leading-tight truncate">{m.title}</div>
                      <div className="text-[10px] text-white/80 truncate">{m.subtitle}</div>
                    </div>
                    <ChevronRight className={`w-4 h-4 transition ${isActive ? 'rotate-90' : 'group-hover:translate-x-0.5'}`} />
                  </div>
                  {/* Body */}
                  <div className={`${m.tint} border-t px-3 py-2 flex items-center justify-between h-full`}>
                    <span className="text-[11px] font-semibold uppercase tracking-wide opacity-80">Abrir módulo</span>
                    {m.badge && (
                      <span className="text-[9px] font-bold uppercase px-2 py-0.5 rounded-full bg-white/70 shadow-sm">
                        {m.badge}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}

            {/* Utility column - actions */}
            {(onAction.brochure || onAction.fixLinks || onAction.logout) && (
              <div className={`snap-start flex-shrink-0 ${cardWidth} rounded-xl border-2 border-dashed border-gray-300 bg-white p-2 flex flex-col gap-1.5`}>
                {onAction.fixLinks && (
                  <button
                    onClick={onAction.fixLinks}
                    className="text-[11px] px-2 py-1.5 rounded-md bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 flex items-center gap-1"
                    data-testid="action-fix-links"
                  >
                    <RefreshCw className="w-3 h-3" /> Reparar enlaces
                  </button>
                )}
                {onAction.brochure && (
                  <button
                    onClick={onAction.brochure}
                    className="text-[11px] px-2 py-1.5 rounded-md bg-sky-50 text-sky-700 border border-sky-200 hover:bg-sky-100 flex items-center gap-1"
                    data-testid="action-brochure"
                  >
                    <BookOpen className="w-3 h-3" /> Brochure
                  </button>
                )}
                {onAction.logout && (
                  <button
                    onClick={onAction.logout}
                    className="text-[11px] px-2 py-1.5 rounded-md bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 flex items-center gap-1 mt-auto"
                    data-testid="action-logout"
                  >
                    <LogOut className="w-3 h-3" /> Salir
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ModuleKanbanMenu;
